import {RemoteI9Details, RepresentativeCandidateDocumentDetails} from '@dtos/remote-i9-details';
import {OnboardingDetailsRequestEvent} from '@models/onboarding-review/onboarding-details-request-event';
import {ISection2DocumentDetails} from '@models/onboarding-review/section2-document-details';
import {IConfigurationValuesService} from '@services/ConfigurationValuesService';
import {IOnboardingDetailsRequestService} from '@services/onboarding-review/onboarding-details-request-service';
import {IPersonPidEncryptionService} from '@services/onboarding-review/person-pid-encryption-service';
import {ISection2DocumentsStore} from '@stores/onboarding-review/section2-documents-store';
import { I9DocumentInfoDto } from '@swipejobs/i9-document-validations-react';
import {Subscription} from 'rxjs';

export interface ISection2DocumentsUpdater {
    initialise(): void;
    cleanup(): void;
}

export class Section2DocumentsUpdater implements ISection2DocumentsUpdater {
    private requestEventsSubscription: Subscription = Subscription.EMPTY;

    constructor(
        private store: ISection2DocumentsStore,
        private onboardingDetailsRequestService: IOnboardingDetailsRequestService,
        private personPidEncryptionService: IPersonPidEncryptionService,
        private configurationValuesService: IConfigurationValuesService
    ) {
    }

    initialise(): void {
        this.requestEventsSubscription = this.onboardingDetailsRequestService
            .requestEvents
            .subscribe(this.listenToRequestEvents);
    }

    listenToRequestEvents = (event: OnboardingDetailsRequestEvent) => {
        if (event.type === 'pending') {
            this.handlePending();
            return;
        }

        if (event.type === 'success') {
            this.handleSuccess(event.dto);
            return;
        }

        if (event.type === 'documentTypesSuccess') {
            this.handleDocumentTypeSuccess(event.dto);
            return;
        }

        if (event.type === 'workerStatusSuccess') {
            this.handleWorkerStatusSuccess(event.dto);
        }
    };

    handlePending() {
        this.store.setDocuments([]);
    };

    handleSuccess({ documentDetails }: RemoteI9Details) {
        const prepopulateI9DocumentDetails = this.configurationValuesService.prepopulateI9DocumentDetails;
        const convertedDocumentDetails: ISection2DocumentDetails[] = (
            documentDetails.reduce<ISection2DocumentDetails[]>((documents, details) => {
                // prevents nil documents from being converted and included
                if (!details) {
                    return documents;
                }

                return [...documents, this.prepareDocumentDetails(details, prepopulateI9DocumentDetails)];
            }, [])
        );
        this.store.setDocuments(convertedDocumentDetails);
    }

    handleDocumentTypeSuccess(documentValidationTypes: I9DocumentInfoDto[]) {
        this.store.setDocumentTypesValidation(documentValidationTypes);
    }

    handleWorkerStatusSuccess( workerStatus: string | null) {
        this.store.setWorkerStatus(workerStatus);
    }

    cleanup(): void {
        this.requestEventsSubscription.unsubscribe();
    }

    private prepareDocumentDetails(
        // must not be nil
        details: RepresentativeCandidateDocumentDetails,
        shouldPrepopulate: boolean
    ): ISection2DocumentDetails {
        if (!shouldPrepopulate) {
            return {
                ...details,
                issuingAuthority: '',
                issuingAuthorityCode: '',
                expiryDate: '',
                number: '',
                expiryDateNAChecked: false
            };
        }

        return {
            ...details,
            issuingAuthority: details.issuingAuthority,
            expiryDateNAChecked: details.expiryDateNAChecked ?? false,
            issuingAuthorityCode: details.issuingAuthorityCode ?? '',
            expiryDate: details.expiryDate || null,
            number: this.personPidEncryptionService.decryptDto(details.number)
        };
    }
}



// WEBPACK FOOTER //
// ./src/stores/onboarding-review/section2-documents-updater.ts